import { useCallback, useEffect, useRef, useState } from 'react';

export interface Remote<T> {
    data: T | null;
    state: 'loading' | 'error' | 'ready';
    reload: () => void;
}

interface Slot<T> {
    key: string | null;
    data: T | null;
    failed: boolean;
}

/**
 * Loads `load()` whenever `key` changes. `key === null` means idle (state "ready", no data).
 * With `refreshMs` it reloads quietly while the tab is visible; a failed refresh keeps the last data.
 */
export function useRemote<T>(key: string | null, load: () => Promise<T>, refreshMs?: number): Remote<T> {
    const loadRef = useRef(load);
    const [slot, setSlot] = useState<Slot<T>>({ key: null, data: null, failed: false });
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        loadRef.current = load;
    });

    useEffect(() => {
        if (key === null) return;
        let live = true;
        const run = () =>
            loadRef.current().then(
                (data) => {
                    if (live) setSlot({ key, data, failed: false });
                },
                () => {
                    if (live) setSlot((prev) => (prev.key === key && prev.data !== null ? prev : { key, data: null, failed: true }));
                }
            );
        void run();
        const timer = refreshMs
            ? window.setInterval(() => {
                  if (!document.hidden) void run();
              }, refreshMs)
            : undefined;
        return () => {
            live = false;
            window.clearInterval(timer);
        };
    }, [key, refreshMs, attempt]);

    const reload = useCallback(() => {
        setSlot({ key: null, data: null, failed: false });
        setAttempt((n) => n + 1);
    }, []);

    if (key === null) return { data: null, state: 'ready', reload };
    if (slot.key !== key) return { data: null, state: 'loading', reload };
    return { data: slot.data, state: slot.failed ? 'error' : 'ready', reload };
}
